import * as client from "../../../client";
import { DBPerilType } from "../general-input/util";
import { PERIL_TYPE_ORDER } from "./util";

export type PerilEntry = {
  perilType: DBPerilType;
  perilCode: string;
  value: number;
  max: number;
  /** Effective premium for this peril code at modal-open. */
  initial: number;
  /** AAL for the code, or 0 if `multiple` doesn't apply (direct-premium codes). */
  aal: number;
  /** Locked rows are skipped during proportional rescaling on target edits.
   * Manual slider drags still take effect. */
  locked: boolean;
};

/** Slider max sits 3× the value at modal-open so the thumb starts at ~33% and
 * has room to move right under most target edits before a rescale is needed. */
export const SLIDER_HEADROOM = 3;

/** The commission factor cf = (1 − 0.175) / (1 − commission/100) as an exact
 * integer ratio `num/den`, mirroring the backend's `_commission_factor`. `null`
 * commission is the default 17.5 % — no adjustment (cf = 1). */
const commissionRatio = (
  commissionPercent: number | null
): { num: number; den: number } => {
  if (commissionPercent == null) return { num: 1, den: 1 };
  // Scale both terms by 10 000 so 0.825 and percentages of up to two decimals
  // stay integers; the resulting products stay exact within MAX_SAFE_INTEGER.
  // Working from the ratio reproduces the backend's once-rounded rational math
  // rather than drifting on float multiplication.
  return { num: 8250, den: 10000 - Math.round(commissionPercent * 100) };
};

/** Round non-negative `num/den` to the nearest integer, ties to even — matching
 * the banker's rounding Python's `round()` applies to the layer total, so the
 * dialog and backend agree on exact halves. `num`/`den` must be non-negative
 * integers. */
const bankersRoundRatio = (num: number, den: number): number => {
  const q = Math.floor(num / den);
  const twiceRem = (num - q * den) * 2;
  if (twiceRem > den) return q + 1;
  if (twiceRem < den) return q;
  return q % 2 === 0 ? q : q + 1;
};

/** The quoted (commission-adjusted) layer total the backend will save for a
 * `baseTotal` premium sum, computed the same way the backend does so the
 * dialog's headline always matches the saved value. */
export const toQuotedTotal = (
  baseTotal: number,
  commissionPercent: number | null
): number => {
  const { num, den } = commissionRatio(commissionPercent);
  const hundredths = Math.round(baseTotal * 100);
  return bankersRoundRatio(hundredths * num, den * 100);
};

/** The base-premium sum whose quoted total is `quotedTotal` (i.e. T / cf), the
 * inverse of {@link toQuotedTotal}. Returned at full precision: callers must
 * distribute it across perils at cent precision, or the round-trip back through
 * the commission factor can land a dollar off. */
export const toBaseTotal = (
  quotedTotal: number,
  commissionPercent: number | null
): number => {
  const { num, den } = commissionRatio(commissionPercent);
  return (quotedTotal * den) / num;
};

export const recomputeMax = (current: number, value: number): number =>
  value > current ? Math.max(1, Math.round(value * SLIDER_HEADROOM)) : current;

/** Distribute `target` (integer) across slots in proportion to `weights`,
 * returning integers that sum exactly to `target`. Uses largest-remainder so
 * no fractional dollars are lost. All-zero weights fall back to an equal
 * split (remainder lands on the earliest slots, stable). */
export const distributeProportionally = (
  weights: number[],
  target: number
): number[] => {
  if (weights.length === 0) return [];
  const sum = weights.reduce((s, w) => s + w, 0);
  const effective = sum > 0 ? weights : weights.map(() => 1);
  const effectiveSum = sum > 0 ? sum : weights.length;
  const scale = target / effectiveSum;

  const items = effective.map((w, i) => {
    const exact = w * scale;
    const floor = Math.floor(exact);
    return { i, floor, remainder: exact - floor };
  });
  const deficit = target - items.reduce((s, x) => s + x.floor, 0);

  // The `deficit` items with the largest remainders each absorb +1.
  const bumped = new Set(
    items
      .toSorted((a, b) => b.remainder - a.remainder)
      .slice(0, deficit)
      .map((x) => x.i)
  );
  return items.map((x) => x.floor + (bumped.has(x.i) ? 1 : 0));
};

export const buildInitialEntries = (
  premium: client.LayerPremiumBreakdown | undefined
): PerilEntry[] => {
  if (!premium) return [];
  return Object.entries(premium.peril_types)
    .flatMap(([perilType, optionsBySource]): PerilEntry[] => {
      return optionsBySource.resolved.parts.map((part) => {
        const current = Math.round(part.layer_effective);
        return {
          perilType: perilType as DBPerilType,
          perilCode: part.peril_code,
          value: current,
          max: Math.max(1, Math.round(current * SLIDER_HEADROOM)),
          initial: current,
          aal: part.multiplier !== null ? part.aal : 0,
          locked: false,
        };
      });
    })
    .toSorted((a, b) => {
      const ai = PERIL_TYPE_ORDER.indexOf(a.perilType);
      const bi = PERIL_TYPE_ORDER.indexOf(b.perilType);
      const at = ai === -1 ? Infinity : ai;
      const bt = bi === -1 ? Infinity : bi;
      if (at !== bt) return at - bt;
      return a.perilCode.localeCompare(b.perilCode);
    });
};

/** Rescale all unlocked entries so the total equals `newTotal`. Locked entries
 * keep their current value. A fractional `newTotal` (e.g. the base sum `T / cf`
 * for a quoted target) is preserved to the cent. Returns the original array
 * unchanged when the new total matches the current total, when there are no
 * unlocked entries, or when the unlocked entries currently sum to zero. */
export const applyTargetTotal = (
  entries: PerilEntry[],
  newTotal: number
): PerilEntry[] => {
  const cents = (v: number) => Math.round(v * 100);
  const totalCents = entries.reduce((s, e) => s + cents(e.value), 0);
  const newCents = cents(newTotal);
  if (newCents === totalCents) return entries;
  const lockedCents = entries
    .filter((e) => e.locked)
    .reduce((s, e) => s + cents(e.value), 0);
  if (totalCents - lockedCents <= 0) return entries;
  const unlockedTargetCents = Math.max(0, newCents - lockedCents);

  const distributed = distributeProportionally(
    entries.filter((e) => !e.locked).map((e) => cents(e.value)),
    unlockedTargetCents
  );

  let k = 0;
  return entries.map((e) => {
    if (e.locked) return e;
    const v = (distributed[k++] ?? cents(e.value)) / 100;
    return { ...e, value: v, max: recomputeMax(e.max, v) };
  });
};

/** Apply a slider move. When `targetLocked` is false the moved entry takes
 * `rawValue` (snapped to its initial value if very close). When true, the
 * other unlocked entries absorb the delta proportionally, with the moved
 * value clamped so none can go negative. */
export const applySliderChange = (
  entries: PerilEntry[],
  perilCode: string,
  rawValue: number,
  targetLocked: boolean
): PerilEntry[] => {
  const movedIdx = entries.findIndex((e) => e.perilCode === perilCode);
  const moved = entries[movedIdx];
  if (!moved) return entries;

  // Snap to the initial (saved) value if the slider lands within half a step
  // of it. The slider's grid is 1% of max, so initial is otherwise
  // unreachable unless it happens to align.
  const step = Math.max(1, Math.round(moved.max / 100));
  const intended =
    Math.abs(rawValue - moved.initial) < step / 2 ? moved.initial : rawValue;

  if (!targetLocked) {
    return entries.map((e, i) =>
      i === movedIdx
        ? { ...e, value: intended, max: recomputeMax(e.max, intended) }
        : e
    );
  }

  const others = entries.filter((e, i) => i !== movedIdx && !e.locked);
  if (others.length === 0) return entries; // nothing to compensate with

  // Work in integer cents, like applyTargetTotal, so the redistribution sums
  // exactly. After a target edit the values carry cents; computing the
  // compensation in whole dollars would floor sub-dollar remainders away on
  // every drag, ratcheting the total down a dollar at a time.
  const cents = (v: number) => Math.round(v * 100);
  const otherSumCents = others.reduce((s, o) => s + cents(o.value), 0);
  const movedCents = cents(moved.value);
  // Clamp the moved slider so others can absorb the delta without going
  // negative. Up: limited by otherSum. Down: limited by 0.
  const clampedNewCents = Math.max(
    0,
    Math.min(movedCents + otherSumCents, cents(intended))
  );
  const otherTargetCents = otherSumCents - (clampedNewCents - movedCents);

  const distributed = distributeProportionally(
    others.map((o) => cents(o.value)),
    otherTargetCents
  );

  let k = 0;
  return entries.map((e, i) => {
    if (i === movedIdx) {
      const v = clampedNewCents / 100;
      return { ...e, value: v, max: recomputeMax(e.max, v) };
    }
    if (e.locked) return e;
    const v = (distributed[k++] ?? cents(e.value)) / 100;
    return { ...e, value: v, max: recomputeMax(e.max, v) };
  });
};

export const resetEntries = (entries: PerilEntry[]): PerilEntry[] =>
  entries.map((e) => ({
    ...e,
    value: e.initial,
    max: recomputeMax(e.max, e.initial),
    locked: false,
  }));
